/* eslint no-param-reassign: 0 */

import produce from 'immer';
import {flatMap, partition} from 'lodash';

import {RuleCompoundConditionTypesEnum} from '../../models/api/ruleModel';
import {WithClientId} from '../editors/commons/clientIdModel';
import {checkANDConditionsEquality, fromDefinitionCondition} from './ruleConditionHelpers';
import {
  ANDConditions,
  AssigneeChanged,
  AssigneeIn,
  RuleCondition,
  RuleConditionTypesEnum,
} from './ruleConditionModel';
import {RuleState} from './ruleEditor/state/ruleEditorState';
import {RuleIntegration, SimpleCondition} from './ruleIntegrationModel';
import {
  RuleTriggerEventTypes,
  RuleTriggerFromEventsDescription,
  RuleTriggersTypesEnum,
  triggersConditionsMap,
} from './ruleTriggerModel';

export interface TriggerWithConditions {
  trigger: RuleTriggersTypesEnum;
  conditions: ANDConditions;
}

export function triggersFromEvents(
  events: ReadonlyArray<RuleTriggerEventTypes>,
): ReadonlyArray<RuleTriggersTypesEnum> {
  return (
    // Object.entries loses precision on the key type.
    (
      Object.entries(RuleTriggerFromEventsDescription) as ReadonlyArray<
        [RuleTriggersTypesEnum, RuleTriggerFromEventsDescription]
      >
    )
      .filter(([, desc]) => desc.events.every((event) => events.includes(event)))
      .map(([trigger]) => trigger)
  );
}

// Picks up all conditions attached to triggers and bundle them into 'TriggerWithConditions' objects.
// Also returns the leftover list of conditions stripped of the former conditions.
export function buildTriggersWithConditions(
  triggers: ReadonlyArray<RuleTriggersTypesEnum>,
  allRuleConditions: ANDConditions,
): {triggersWithConditions: ReadonlyArray<TriggerWithConditions>; remainingConditions: ANDConditions} {
  const seed = {
    triggersWithConditions: [] as ReadonlyArray<TriggerWithConditions>,
    availableConditions: allRuleConditions,
  };

  const result = triggers.reduce<typeof seed>((acc, trigger) => {
    const {triggerConditions, remainingConditions: rest} = conditionsForTrigger(
      trigger,
      acc.availableConditions,
    );
    const triggerWithConditions = {trigger, conditions: triggerConditions};

    return {
      triggersWithConditions: acc.triggersWithConditions.concat([triggerWithConditions]),
      // The next triggers can only pick among the conditions that aren't already bound to another trigger.
      availableConditions: rest,
    };
  }, seed);

  return {
    triggersWithConditions: result.triggersWithConditions,
    remainingConditions: result.availableConditions.filter((orConds) => orConds.length > 0),
  };
}

export function triggerWithInitialConditions(
  trigger: RuleTriggersTypesEnum,
  ruleIntegration: RuleIntegration,
): TriggerWithConditions {
  switch (trigger) {
    case RuleTriggersTypesEnum.COMMENT:
    case RuleTriggersTypesEnum.MENTION:
    case RuleTriggersTypesEnum.ASSIGN:
    case RuleTriggersTypesEnum.TAG:
    case RuleTriggersTypesEnum.UNTAG: {
      const conditionTypes = triggersConditionsMap[trigger] ?? [];

      const conditions = conditionTypes.map(({type, optional}) => {
        const definitionCondition = ruleIntegration.findConditionDefinition({
          type,
          integration_id: 0,
        } as SimpleCondition);

        if (optional || !definitionCondition) {
          return [];
        }

        return [fromDefinitionCondition(definitionCondition)];
      });

      return {trigger, conditions};
    }

    // eslint-disable-next-line no-fallthrough
    default:
      return {trigger, conditions: []};
  }
}

/**
 * Tries to find conditions bound to a trigger (see: triggersConditionsMap)
 *
 * Example :
 * Inputs
 * - trigger: ['tag', 'inbound']
 * - allRuleconditions: ['tag_added_is', 'body_contains']
 *
 * => Outputs
 * - triggerConditions: [
 *  {trigger: 'tag', conditions: ['tag_added_is']},
 *  {trigger: 'inbound', conditions: []}
 * ]
 * - remainingConditions: ['body_contains']
 */
function conditionsForTrigger(
  trigger: RuleTriggersTypesEnum,
  allRuleConditions: ANDConditions,
): {triggerConditions: ANDConditions; remainingConditions: ANDConditions} {
  const triggerConditionTypes: ReadonlyArray<RuleConditionTypesEnum> =
    triggersConditionsMap[trigger]?.map(({type}) => type) ?? [];

  const result = produce(
    {
      conditions: allRuleConditions,
      triggerConditions: [] as ANDConditions,
    },
    (draft) => {
      // Look for the first matching condition.
      triggerConditionTypes.some((conditionType) => {
        const matchesConditionType = (condition: RuleCondition) => condition.type === conditionType;

        let matchingORIndex = -1;
        const matchingANDIndex = draft.conditions.findIndex((orConds) => {
          matchingORIndex = orConds.findIndex(matchesConditionType);
          return matchingORIndex >= 0;
        });

        if (matchingANDIndex < 0 || matchingORIndex < 0) {
          return false;
        }

        // Filter all the conditions in the 'OR' node that match. There could be several ones.
        // e.g : 'comment author is X OR Y OR Z'
        const [matchingConditions, otherConditions] = partition(
          draft.conditions[matchingANDIndex],
          matchesConditionType,
        );

        // The leftover conditions stay in the 'regular' rule conditions...
        draft.conditions[matchingANDIndex] = otherConditions;

        // And the matching conditions are bundled under the trigger conditions.
        if (!draft.triggerConditions[0]) {
          draft.triggerConditions[0] = [];
        }

        draft.triggerConditions[0].push(...matchingConditions);

        /**
         * Assignee changed only takes one condition.
         * This avoid to have conflict between assignee_changed and assignee_in (legacy trigger condition).
         */
        if (
          matchingConditions.some(
            (matchingCondition) => matchingCondition.type === RuleConditionTypesEnum.ASSIGNEE_CHANGED,
          )
        ) {
          return true;
        }

        return false;
      });
    },
  );

  return {
    triggerConditions: result.triggerConditions,
    remainingConditions: result.conditions,
  };
}

export function checkTriggersWithConditionsEquality(a: TriggerWithConditions, b: TriggerWithConditions) {
  return a.trigger === b.trigger && checkANDConditionsEquality(a.conditions, b.conditions);
}

/**
 * Extract conditions from triggers.
 */
export function extractTriggerConditionsFromState(
  ruleState: RuleState,
  shouldWrapTriggerConditions?: boolean,
) {
  const {triggers} = ruleState;

  if (!shouldWrapTriggerConditions) {
    return flatMap(
      triggers.filter(({conditions}) => conditions.length > 0),
      ({conditions}) =>
        conditions
          .filter((orConds) => orConds.length > 0)
          .map((orConds) => [RuleCompoundConditionTypesEnum.OR, ...orConds]),
    );
  }

  // If `shouldWrapTriggerConditions` is truthy, we're wrapping all trigger conditions in an OR statement,
  // to avoid spreading them in the top-level AND one.
  const mergedTriggerConditions = flatMap(
    triggers.filter(({conditions}) => conditions.length > 0),
    ({conditions}) =>
      flatMap(conditions, (orConds) =>
        orConds.map((condition) => {
          if (condition.type !== RuleConditionTypesEnum.ASSIGNEE_IN) {
            return condition;
          }

          return updateAssignInTrigger(condition);
        }),
      ),
  );

  if (mergedTriggerConditions.length === 0) {
    return [];
  }

  return [[RuleCompoundConditionTypesEnum.OR, ...mergedTriggerConditions]];
}

// Update AssigneeIn to AssigneeChanged (this condition is only used for the assign trigger).
function updateAssignInTrigger(condition: WithClientId<AssigneeIn>): WithClientId<AssigneeChanged> {
  return {
    ...condition,
    type: RuleConditionTypesEnum.ASSIGNEE_CHANGED,
  };
}

export function eventsFromTriggers(
  triggers: ReadonlyArray<RuleTriggersTypesEnum>,
): ReadonlyArray<RuleTriggerEventTypes> {
  const result = new Set<RuleTriggerEventTypes>();

  triggers.forEach((trigger) =>
    RuleTriggerFromEventsDescription[trigger]?.events.forEach((event) => result.add(event)),
  );

  return Array.from(result);
}
